/**
 * Document Service
 * Cliente da API do motor de emissão de documentos.
 *
 * @version 2026.1.0
 * @description Tipos emissíveis, itens de assinatura, prévia e geração do arquivo
 */
import apiClient from './apiClient';
import type { ApiResponse } from '../types/common';
import type {
  AvailableSignatureItems,
  DocumentEmissionRecord,
  DocumentEmissionRevision,
  DocumentFormat,
  DocumentTypeSummary,
  EmissionListFilters,
  EmissionListResult,
  EmissionRequest,
  EmissionRevisionKey,
  EmissionSummary,
  GeneratedDocumentFile,
  PreviewResult,
  ReportType,
} from '../types/document';

/**
 * O envelope da listagem: o do projeto (`data` + `pagination`) mais o `summary`, que é
 * exclusivo desta rota e por isso não cabe no `ApiResponse` comum.
 */
type EmissionListEnvelope = ApiResponse<DocumentEmissionRecord[]> & { summary?: EmissionSummary };

const SEM_CONTAGEM: EmissionSummary = { vigente: 0, aVencer: 0, vencido: 0, semValidade: 0 };

// Re-export types for convenience
export * from '../types/document';

class DocumentService {
  private baseUrl = '/documents';

  /**
   * Tipos emissíveis. Com `companyId`, só os do módulo que a empresa contratou —
   * por isso a tela escolhe a empresa antes do tipo.
   */
  async listTypes(companyId?: number): Promise<DocumentTypeSummary[]> {
    const query = companyId === undefined ? '' : `?companyId=${companyId}`;
    const response = await apiClient.get<DocumentTypeSummary[]>(`${this.baseUrl}/types${query}`);
    return response.data ?? [];
  }

  /**
   * Profissionais e declarações que podem compor o bloco de assinaturas.
   *
   * Os ids chegam como `number` porque `SignatoryResolver` já converte os
   * `BIGINT` que o driver devolve como string — a correção mora na ORIGEM, de
   * propósito, para nenhum cliente novo precisar redescobri-la. Se um id voltar
   * a chegar como `"12"`, a emissão responde 400 (`documentValidator` exige
   * inteiro): o conserto é lá no resolver, NÃO uma conversão defensiva aqui.
   */
  async listSignatureItems(companyId: number, type: ReportType): Promise<AvailableSignatureItems> {
    const params = new URLSearchParams({ companyId: String(companyId), type });
    const response = await apiClient.get<AvailableSignatureItems>(
      `${this.baseUrl}/signature-items?${params.toString()}`
    );

    return response.data ?? { signatories: [], declarations: [] };
  }

  /**
   * O histórico paginado, só na revisão vigente. As revisões anteriores de cada linha
   * saem de `listRevisions`.
   */
  async listEmissions(filters: EmissionListFilters = {}): Promise<EmissionListResult> {
    const params = new URLSearchParams();
    if (filters.companyId !== undefined) params.set('companyId', String(filters.companyId));
    if (filters.type) params.set('type', filters.type);
    if (filters.periodStart) params.set('periodStart', filters.periodStart);
    if (filters.periodEnd) params.set('periodEnd', filters.periodEnd);
    if (filters.status) params.set('status', filters.status);
    if (filters.page !== undefined) params.set('page', String(filters.page));
    if (filters.limit !== undefined) params.set('limit', String(filters.limit));

    const response = (await apiClient.get<DocumentEmissionRecord[]>(
      `${this.baseUrl}/emissions?${params.toString()}`
    )) as EmissionListEnvelope;

    const data = response.data ?? [];
    return {
      data,
      pagination: response.pagination ?? {
        total: data.length,
        page: 1,
        limit: data.length,
        totalPages: 1,
      },
      summary: response.summary ?? SEM_CONTAGEM,
    };
  }

  /**
   * Todas as revisões de uma chave, da mais nova para a mais antiga.
   *
   * O período vai na consulta porque ele FAZ PARTE da chave: sem ele, o diálogo do PGR de
   * 2026 listaria junto as revisões do PGR de 2025, as duas séries começando em 1.
   */
  async listRevisions(key: EmissionRevisionKey): Promise<DocumentEmissionRevision[]> {
    const params = new URLSearchParams({
      periodStart: key.periodStart,
      periodEnd: key.periodEnd,
    });
    const response = await apiClient.get<DocumentEmissionRevision[]>(
      `${this.baseUrl}/emissions/${key.type}/${key.scope}/${key.scopeId}/revisions?${params.toString()}`
    );

    return response.data ?? [];
  }

  /**
   * Prévia em HTML com a lista INTEIRA de pendências, inclusive as que bloqueiam.
   */
  async preview(request: EmissionRequest): Promise<PreviewResult> {
    const response = await apiClient.post<PreviewResult>(`${this.baseUrl}/preview`, request);
    return response.data ?? { html: '', pendencies: [] };
  }

  /**
   * Gera o arquivo. Usa o axios cru porque o nome do arquivo vem no cabeçalho
   * `Content-Disposition`, que o wrapper do httpClient descarta.
   */
  async generate(request: EmissionRequest, format: DocumentFormat): Promise<GeneratedDocumentFile> {
    const response = await apiClient.getClient().post<Blob>(
      `${this.baseUrl}/generate`,
      { ...request, format },
      { responseType: 'blob' }
    );

    const disposition = response.headers['content-disposition'];
    const filename = this.parseFilename(
      typeof disposition === 'string' ? disposition : undefined,
      format
    );

    return { blob: response.data, filename };
  }

  /** Dispara o download do arquivo no navegador. */
  triggerDownload(file: GeneratedDocumentFile): void {
    const url = window.URL.createObjectURL(file.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  private parseFilename(disposition: string | undefined, format: DocumentFormat): string {
    const fallback = `documento.${format}`;
    if (!disposition) return fallback;

    const match = /filename="?([^";]+)"?/i.exec(disposition);
    return match?.[1] ?? fallback;
  }
}

// Export singleton instance
export const documentService = new DocumentService();
export default documentService;
